import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";

const roleOptions = [
  { value: "AI Video Creator", label: "AI Video Creator" },
  { value: "Scriptwriter", label: "Scriptwriter / Screenwriter (Non-AI)" },
  { value: "Indie Filmmaker", label: "Film Director / Indie Filmmaker" },
  { value: "Voice Artist", label: "Voice Artist (AI or Human)" },
  { value: "Funder", label: "Producer / Funder" },
];

const genreOptions = [
  "Sci-Fi",
  "Fantasy",
  "Comedy",
  "Drama",
  "Horror",
  "Romance",
  "Kids",
  "Adventure",
  "Documentary",
  "Music Video",
  "Experimental",
].map((g) => ({ value: g, label: g }));

const toolOptions = [
  "RunwayML",
  "Pika Labs",
  "Midjourney",
  "Kling",
  "ElevenLabs",
  "Luma AI",
  "ChatGPT",
  "Synthesia",
  "Descript",
  "Veed.io",
  "Invideo",
  "Lumen5",
  "Animoto",
  "SeaArt",
  "VEO3",
].map((t) => ({ value: t, label: t }));

const toneOptions = [
  "Whimsical",
  "Dark",
  "Corporate",
  "Inspirational",
  "Lighthearted",
  "Realistic",
  "Melancholic",
  "Futuristic",
];

const timeZoneOptions = [
  "UTC−08:00 Pacific Time (US & Canada)",
  "UTC−07:00 Mountain Time (US & Canada)",
  "UTC−06:00 Central Time (US & Canada)",
  "UTC−05:00 Eastern Time (US & Canada)",
  "UTC±00:00 UTC / GMT",
  "UTC+01:00 Central European Time",
  "UTC+05:30 India Standard Time",
  "UTC+08:00 Singapore Time",
  "UTC+10:00 Australia Eastern Time",
  "UTC−03:00 Brasilia Time",
  "UTC−06:00 Central Time (Mexico City)",
];

const collabStyleOptions = ["Asynchronous", "Real-time", "Hybrid (Both)"];

const Profile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isEditMode = location.pathname.includes("/edit");
  const [userId, setUserId] = useState(null);
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  // NEW: track whether this user already has a saved creator profile
  const [hasExistingProfile, setHasExistingProfile] = useState(false);

  const [profile, setProfile] = useState({
    name: "",
    first_name: "",
    last_name: "",
    tagline: "",
    role: "",
    genres: [],
    tools: [],
    tone: "",
    availability: "",
    time_zone: "",
    collab_style: "",
    ai_reel: "",
    portfolio_links: "",
    bio: "",
    profile_image_url: "",
  });

  useEffect(() => {
    const fetchUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
        const { data } = await supabase
          .from("creator_profiles")
          .select("*")
          .eq("user_id", user.id)
          .single();

        if (data) {
          // We already have a profile on record
          setHasExistingProfile(true);

          const existingFullName = data.name || "";
          let firstName = data.first_name || "";
          let lastName = data.last_name || "";

          // Fallback: derive first/last from name if needed
          if ((!firstName || !lastName) && existingFullName) {
            const parts = existingFullName.trim().split(" ");
            if (!firstName) firstName = parts[0] || "";
            if (!lastName) lastName = parts.slice(1).join(" ") || "";
          }

          setProfile({
            ...data,
            first_name: firstName,
            last_name: lastName,
            genres:
              data.genres
                ?.split(",")
                ?.map((g) => ({ value: g.trim(), label: g.trim() })) || [],
            tools:
              data.tools
                ?.split(",")
                ?.map((t) => ({ value: t.trim(), label: t.trim() })) || [],
            role: roleOptions.find((r) => r.value === data.role) || "",
          });
          setProfileImageUrl(data.profile_image_url || "");
        } else if (!isEditMode) {
          // First-time setup – prefill from auth metadata
          const fullName = user.user_metadata?.full_name || "";
          const parts = fullName.trim().split(" ");
          const firstName = parts[0] || "";
          const lastName = parts.slice(1).join(" ");

          setProfile((prev) => ({
            ...prev,
            name: fullName,
            first_name: firstName,
            last_name: lastName,
          }));
        }
      }
    };
    fetchUser();
  }, [isEditMode]);

  useEffect(() => {
    let filled = 0;

    // Treat first + last name as one completeness field
    if (profile.first_name?.trim() && profile.last_name?.trim()) filled++;
    if (profile.tagline?.trim()) filled++;
    if (profile.profile_image_url?.trim()) filled++;
    if (profile.role?.value?.trim()) filled++;
    if (profile.genres?.length > 0) filled++;
    if (profile.tools?.length > 0) filled++;
    if (profile.tone?.trim()) filled++;
    if (profile.ai_reel?.trim()) filled++;
    if (profile.portfolio_links?.trim()) filled++;
    if (profile.availability?.trim()) filled++;
    if (profile.time_zone?.trim()) filled++;
    if (profile.collab_style?.trim()) filled++;
    if (profile.bio?.trim()) filled++;

    const totalFields = 13;
    setProgress(Math.round((filled / totalFields) * 100));
  }, [profile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (field, selected) => {
    setProfile((prev) => ({ ...prev, [field]: selected }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !userId) return;

    const fileExt = file.name.split(".").pop();
    const filePath = `avatars/${userId}.${fileExt}`;
    const { error } = await supabase.storage
      .from("avatars")
      .upload(filePath, file, { upsert: true });
    if (error) return alert("Image upload failed: " + error.message);

    const { data: urlData } = supabase.storage
      .from("avatars")
      .getPublicUrl(filePath);
    setProfile((prev) => ({ ...prev, profile_image_url: urlData.publicUrl }));
    setProfileImageUrl(urlData.publicUrl);
  };

  const validateFields = () => {
    const firstName = profile.first_name?.trim();
    const lastName = profile.last_name?.trim();

    if (!firstName || !lastName) {
      return "Please enter your full name (first and last).";
    }
    if (profile.tagline.trim().length < 10)
      return "Tagline must be at least 10 characters.";
    if (profile.bio.trim().length < 30)
      return "Bio must be at least 30 characters.";
    if (profile.portfolio_links && !profile.portfolio_links.includes("http"))
      return "Portfolio links should be valid URLs.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    const error = validateFields();
    if (error) return setErrorMsg(error);

    const trimmedFirst = profile.first_name?.trim() || "";
    const trimmedLast = profile.last_name?.trim() || "";
    const fullName = [trimmedFirst, trimmedLast].filter(Boolean).join(" ");

    // Base payload for both insert and update
    let payload = {
      ...profile,
      user_id: userId,
      role: profile.role?.value || "",
      genres: profile.genres.map((g) => g.value).join(", "),
      tools: profile.tools.map((t) => t.value).join(", "),
    };

    if (!hasExistingProfile) {
      // First-time save: write name fields
      payload.name = fullName;
      payload.first_name = trimmedFirst;
      payload.last_name = trimmedLast;
    } else {
      // Subsequent edits: do NOT update name/first_name/last_name
      delete payload.name;
      delete payload.first_name;
      delete payload.last_name;
    }

    const {
      data: existingProfile,
      error: fetchError,
    } = await supabase
      .from("creator_profiles")
      .select("id")
      .eq("user_id", userId)
      .single();

    if (fetchError && fetchError.code !== "PGRST116") {
      alert(`Failed to check existing profile: ${fetchError.message}`);
      return;
    }

    let result;
    if (existingProfile) {
      result = await supabase
        .from("creator_profiles")
        .update(payload)
        .eq("user_id", userId);
    } else {
      result = await supabase.from("creator_profiles").insert([payload]);
    }

    if (result.error) {
      alert(`Failed to save profile: ${result.error.message}`);
    } else {
      navigate("/profile-review");
    }
  };

  const handleCancel = () => {
    alert("Profile editing cancelled.");
    navigate("/profile-review");
  };

  return (
    <div className="min-h-full bg-transparent text-slate-100">
      <div className="mx-auto max-w-5xl px-4 py-6 md:py-8">
        {/* Heading + banner */}
        <h2 className="text-xl font-semibold text-cyan-300 md:text-2xl">
          {isEditMode ? "✏️ Edit Your Profile" : "👤 Profile Setup"}
        </h2>

        <div className="mt-3 rounded-2xl border border-slate-700/60 bg-slate-900/80 px-4 py-3 text-xs text-indigo-100 shadow-lg shadow-fuchsia-500/20 backdrop-blur">
          🧩{" "}
          <span className="font-semibold">
            On Dreamframe, every user can both create and collaborate.
          </span>{" "}
          Post projects as a Creator or join others as a Collaborator — no
          separate accounts needed.
        </div>

        <p className="mt-2 text-sm text-slate-300">
          Complete your profile so creators and collaborators can find and
          invite you to projects.
        </p>

        {/* Progress bar */}
        <div className="mt-4">
          <div className="relative h-2 overflow-hidden rounded-full bg-slate-700">
            <div
              className="h-full bg-cyan-400 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="mt-1 text-right text-xs text-slate-400">
            {progress}% Complete
          </div>
          <p className="mt-1 text-[11px] text-slate-300">
            Profiles with <strong>80%+</strong> completion appear higher in
            search and recommendations.
          </p>
        </div>

        {/* Avatar preview */}
        {profileImageUrl && (
          <div className="mt-4 flex items-center gap-3">
            <img
              src={profileImageUrl}
              alt="Avatar"
              className="h-20 w-20 rounded-full border-2 border-cyan-400/60 object-cover shadow-md shadow-cyan-500/40"
            />
            <span className="text-xs text-slate-300">
              This is how your profile image appears in search and invites.
            </span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          {/* Basic info */}
          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 shadow-md shadow-fuchsia-500/10 backdrop-blur">
            <h4 className="text-sm font-semibold text-slate-100">
              Basic Info
            </h4>
            <div className="mt-3 space-y-3">
              <div className="grid gap-3 md:grid-cols-2">
                <input
                  name="first_name"
                  value={profile.first_name}
                  onChange={handleChange}
                  placeholder="First Name"
                  required
                  disabled={hasExistingProfile} // LOCK after first save
                  className="w-full rounded-xl border border-slate-600 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-900/60 disabled:text-slate-400"
                />
                <input
                  name="last_name"
                  value={profile.last_name}
                  onChange={handleChange}
                  placeholder="Last Name"
                  required
                  disabled={hasExistingProfile} // LOCK after first save
                  className="w-full rounded-xl border border-slate-600 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-900/60 disabled:text-slate-400"
                />
              </div>

              <p className="text-[11px] text-slate-400">
                Enter your legal first and last name. After your first profile save,
                your name becomes read-only and is used for projects and payments.
                {hasExistingProfile && " Contact support if you need to update it."}
              </p>

              <input
                name="tagline"
                value={profile.tagline}
                onChange={handleChange}
                placeholder="Tagline"
                className="w-full rounded-xl border border-slate-600 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
              />

              <div className="mt-1 inline-flex items-center gap-3">
                <div className="relative inline-block">
                  <button
                    type="button"
                    className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-md shadow-indigo-500/40 hover:bg-indigo-500"
                  >
                    📷 Upload Profile Picture
                  </button>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="absolute inset-0 cursor-pointer opacity-0"
                  />
                </div>
                <span className="text-[11px] text-slate-400">
                  Square images work best. We’ll crop it to a circle.
                </span>
              </div>
            </div>
          </div>

          {/* Creative profile */}
          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 shadow-md shadow-fuchsia-500/10 backdrop-blur">
            <h4 className="text-sm font-semibold text-slate-100">
              Creative Profile
            </h4>
            <p className="mt-1 text-[11px] text-slate-300">
              Tell others what you do best. You can still join projects outside
              your main role.
            </p>

            <div className="mt-3 space-y-3 text-sm">
              <Select
                options={roleOptions}
                value={profile.role}
                onChange={(val) => handleSelectChange("role", val)}
                placeholder="Primary Role / Skill"
                styles={selectStyles}
              />

              <Select
                options={genreOptions}
                isMulti
                value={profile.genres}
                onChange={(val) => {
                  if (val.length > 3) {
                    alert("You can select up to 3 genres only.");
                    return;
                  }
                  handleSelectChange("genres", val);
                }}
                placeholder="Preferred Genres (up to 3)"
                styles={selectStyles}
              />

              <Select
                options={toolOptions}
                isMulti
                value={profile.tools}
                onChange={(val) => {
                  if (val.length > 3) {
                    alert("You can select up to 3 tools only.");
                    return;
                  }
                  handleSelectChange("tools", val);
                }}
                placeholder="Tools You Use (up to 3)"
                styles={selectStyles}
              />

              <select
                name="tone"
                value={profile.tone}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-600 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 focus:outline-none"
              >
                <option value="">Creative Tone / Vibe</option>
                {toneOptions.map((t, i) => (
                  <option key={i} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Collaboration preferences */}
          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 shadow-md shadow-fuchsia-500/10 backdrop-blur">
            <h4 className="text-sm font-semibold text-slate-100">
              Collaboration Preferences
            </h4>
            <p className="mt-1 text-[11px] text-slate-300">
              How you like to work and when you’re generally available.
            </p>

            <div className="mt-3 space-y-3 text-sm">
              <select
                name="availability"
                value={profile.availability}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-600 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 focus:outline-none"
              >
                <option value="">
                  General Availability (e.g. Weekends)
                </option>
                <option value="Weekdays">Weekdays</option>
                <option value="Weekends">Weekends</option>
                <option value="Evenings">Evenings</option>
                <option value="Flexible">Flexible</option>
                <option value="Not Currently Available">
                  Not Currently Available
                </option>
              </select>

              <select
                name="time_zone"
                value={profile.time_zone}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-600 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 focus:outline-none"
              >
                <option value="">Your Time Zone</option>
                {timeZoneOptions.map((tz, i) => (
                  <option key={i} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>

              <select
                name="collab_style"
                value={profile.collab_style}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-600 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 focus:outline-none"
              >
                <option value="">Preferred Work Style</option>
                {collabStyleOptions.map((cs, i) => (
                  <option key={i} value={cs}>
                    {cs}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Showcase */}
          <div className="rounded-2xl border border-slate-700/60 bg-slate-900/80 p-4 shadow-md shadow-fuchsia-500/10 backdrop-blur">
            <h4 className="text-sm font-semibold text-slate-100">Showcase</h4>
            <p className="mt-1 text-[11px] text-slate-300">
              Share links to your best work so others can quickly assess fit.
            </p>

            <div className="mt-3 space-y-3 text-sm">
              <input
                name="ai_reel"
                value={profile.ai_reel}
                onChange={handleChange}
                placeholder="AI Reel or Demo Video URL (optional)"
                className="w-full rounded-xl border border-slate-600 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
              />
              <textarea
                name="portfolio_links"
                value={profile.portfolio_links}
                onChange={handleChange}
                placeholder="Portfolio Links (one per line or comma-separated)"
                rows={2}
                className="w-full rounded-xl border border-slate-600 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
              />
              <textarea
                name="bio"
                value={profile.bio}
                onChange={handleChange}
                placeholder="Short Bio (what you do, strengths, what you’re open to) — max 300 chars"
                rows={3}
                maxLength={300}
                className="w-full rounded-xl border border-slate-600 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
              />
            </div>
          </div>

          {/* Error & buttons */}
          {errorMsg && (
            <p className="text-sm text-rose-400">
              {errorMsg}
            </p>
          )}

          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              className="inline-flex items-center rounded-2xl bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-pink-500 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-fuchsia-500/40 hover:brightness-110"
            >
              Save Profile
            </button>
            {isEditMode && (
              <button
                type="button"
                onClick={handleCancel}
                className="inline-flex items-center rounded-2xl border border-slate-600 bg-slate-900 px-6 py-2.5 text-sm font-semibold text-slate-100 hover:bg-slate-800"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

const selectStyles = {
  container: (base) => ({
    ...base,
    marginBottom: 12,
  }),
  control: (base, state) => ({
    ...base,
    backgroundColor: "#020617",
    borderColor: state.isFocused ? "#22d3ee" : "#475569",
    boxShadow: state.isFocused ? "0 0 0 1px #22d3ee" : "none",
    "&:hover": {
      borderColor: "#22d3ee",
    },
    color: "#e5e7eb",
    minHeight: "38px",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#e5e7eb",
  }),
  input: (base) => ({
    ...base,
    color: "#e5e7eb",
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: "#020617",
    color: "#e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid #334155",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? "#1e293b" : "#020617",
    color: "#e5e7eb",
    "&:active": {
      backgroundColor: "#4f46e5",
    },
    fontSize: 13,
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#1e293b",
    color: "#e5e7eb",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "#e5e7eb",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#e5e7eb",
    ":hover": {
      backgroundColor: "#4f46e5",
      color: "white",
    },
  }),
};

export default Profile;
